import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LOGO_MAX_BYTES } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { blobToDataUrl, useBranding, useClubLogo, writeCachedLogo } from '../club/branding.ts'
import { fitWithin, sha256Hex } from '../photos/capture.ts'
import { BackLink, Banner, errorText } from '../ui/components.tsx'
import { Emblem } from '../ui/Emblem.tsx'
import { Picto } from '../ui/pictos.tsx'
import { useWriteAccess } from '../ui/hooks.ts'

export const LOGO_SIDE = 512

/** The club's logo (M17, ADR-107): office work, online. The picture is redrawn on a canvas at most 512 px on its longest side, as a PNG so that a transparent background stays
 *  transparent (and no metadata goes with it); its hash names it. The server keeps a pending file for that hash, this device uploads the bytes under its own login, the server looks in
 *  storage and confirms. Every device of the club then shows it in its header. */
async function prepareLogo(file: Blob): Promise<{ blob: Blob; sha256: string; bytes: number; mime: 'image/png' | 'image/webp' }> {
  const bitmap = await createImageBitmap(file)
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, LOGO_SIDE)
    const canvas = document.createElement('canvas')
    canvas.width = width; canvas.height = height
    const g = canvas.getContext('2d')
    if (!g) throw new Error('canvas')
    g.drawImage(bitmap, 0, 0, width, height)
    const as = (mime: string) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, 0.92))
    let blob = await as('image/png'); let mime: 'image/png' | 'image/webp' = 'image/png'
    if (!blob || blob.size > LOGO_MAX_BYTES) { blob = await as('image/webp'); mime = 'image/webp' }
    if (!blob || blob.size > LOGO_MAX_BYTES || blob.type !== mime) throw new Error('too big')
    return { blob, sha256: await sha256Hex(blob), bytes: blob.size, mime }
  } finally { bitmap.close() }
}

export function ClubLogoPage() {
  const { t } = useTranslation()
  const { org, sendAndWait, transport } = useClub()
  const access = useWriteAccess('branding.manage')
  const branding = useBranding()
  const current = useClubLogo()
  const input = useRef<HTMLInputElement>(null)
  const [picked, setPicked] = useState<Awaited<ReturnType<typeof prepareLogo>> | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  useEffect(() => { if (!picked) { setPreview(null); return } const u = URL.createObjectURL(picked.blob); setPreview(u); return () => URL.revokeObjectURL(u) }, [picked])

  async function choose(file: File) {
    setError(null); setDone(false)
    try { setPicked(await prepareLogo(file)) } catch { setPicked(null); setError(t('branding.unreadable')) }
  }
  async function save() {
    if (!picked) return
    setBusy(true); setError(null); setDone(false)
    try {
      const v = await sendAndWait('branding.set_logo@1', { sha256: picked.sha256, bytes: picked.bytes, mime: picked.mime })
      if (v.status === 'queued') { setError(t('errors.network')); return }
      if (v.status !== 'applied' && v.status !== 'applied_with_exception') { setError(errorText(t, v.code)); return }
      const s = (v.result?.summary ?? {}) as { file_id?: string; storage_key?: string; upload_state?: string }
      if (!s.file_id || !s.storage_key) { setError(t('errors.unknown')); return }
      if (s.upload_state === 'pending') await transport.uploadAttachment(s.storage_key, picked.blob, picked.mime)
      const c = await sendAndWait('branding.confirm_logo@1', { file_id: s.file_id })
      if (c.status !== 'applied' && c.status !== 'applied_with_exception') { setError(c.status === 'queued' ? t('errors.network') : errorText(t, c.code)); return }
      writeCachedLogo(org.org_id, picked.sha256, await blobToDataUrl(picked.blob))
      setPicked(null); setDone(true)
    } catch (e) { setError(e instanceof Error && e.message ? e.message : t('errors.unknown')) } finally { setBusy(false) }
  }

  return (
    <section className="flex flex-col gap-4">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="palette" className="size-7 text-brand dark:text-fairway" />{t('branding.title')}</h1>
      <p className="opacity-80">{t('branding.help')}</p>
      <div className="turf flex items-center gap-4 rounded-2xl p-4 text-white shadow-sm" data-testid="logo-current">
        {preview ?? current
          ? <span className="inline-flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-1 ring-2 ring-white/70"><img src={(preview ?? current)!} alt={t('branding.alt')} className="size-full object-contain" data-testid="logo-preview" /></span>
          : <Emblem className="size-24 shrink-0" />}
        <span className="min-w-0">
          <span className="block font-serif text-xl font-semibold">{org.org_name}</span>
          <span className="block text-sm text-white/80">{preview ? t('branding.preview') : branding?.logo_sha256 ? t('branding.current') : t('branding.none')}</span>
        </span>
      </div>
      {!access.permitted && <Banner tone="info">{t('branding.notAllowed')}</Banner>}
      {access.permitted && (
        <div className="flex flex-col gap-3">
          <button type="button" className="btn" disabled={busy || !access.allowed} onClick={() => input.current?.click()} data-testid="logo-pick"><Picto name="camera" className="size-5" />{t('branding.pick')}</button>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" tabIndex={-1} aria-hidden data-testid="logo-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void choose(f) }} />
          {picked && <button type="button" className="btn btn-primary" disabled={busy || !access.allowed} onClick={() => void save()} data-testid="logo-save">{busy ? t('branding.saving') : t('branding.save')}</button>}
          <p className="meta">{t('branding.format', { side: LOGO_SIDE })}</p>
        </div>
      )}
      {done && <Banner tone="ok"><span data-testid="logo-done">{t('branding.done')}</span></Banner>}
      {error && <Banner tone="error"><span data-testid="logo-error">{error}</span></Banner>}
    </section>
  )
}
